import { IntlShape } from 'react-intl';

import { LOCAL_DOMAIN } from '~/common/constants/urls';
import { FeatureFlagType } from '~/enums/FeatureFlagType';
import { UserRoleType } from '~/enums/UserRoleType';
import { Nullable } from '~/types/Nullable';
import { PilotId } from '~/types/PilotId';
import { User } from '~/types/User';

type UserNameFields = Partial<Pick<User, 'firstName' | 'lastName'>>;

export const featureFlagIncluded = (
  user: Partial<User>,
  flag: string,
): boolean =>
  Object.keys(user).length !== 0 &&
  !!user.feature_flags &&
  user.feature_flags.includes(flag as FeatureFlagType);

/**
 * Returns the user's name
 */
export function getUserName(
  user: Nullable<UserNameFields> | '' | 0 | false | undefined,
  intl: IntlShape,
): string {
  // TODO: figure out exact specs for removed and unnamed user.  Or even better, never receive this scenario and we can remove the checks here.
  if (!user) {
    return intl.formatMessage({
      id: 'User.removed',
      defaultMessage: 'Removed user',
      description: 'User name for removed user',
    });
  }
  const parts = [user.firstName, user.lastName].filter((part) => part);
  return parts.length > 0
    ? parts.join(' ')
    : intl.formatMessage({
        id: 'User.unnamed',
        defaultMessage: 'Unnamed user',
        description: 'User name for unnamed user',
      });
}

/**
 * Returns the user's initials
 */
export function getUserInitials(user: Partial<User> = {}): string {
  return user.firstName && user.lastName
    ? `${user.firstName[0]}${user.lastName[0]}`
    : 'N/A';
}

/**
 * Formats user info (firstName, lastName, email) as `firstName lastName <email>`.
 * Empty info is omitted. If all info is missing, returns `Unknown`.
 */
export const formatUserNameAndEmail = ({
  firstName,
  lastName,
  email,
}: {
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
}): string => {
  const name = `${firstName || ''} ${lastName || ''}`.trim();
  const displayEmail = email ? `<${email}>` : '';
  return `${name} ${displayEmail}`.trim() || 'Unknown';
};

export function getClientInfo(user?: Partial<User>): number | undefined {
  return user?.client;
}

export function getClientIdWithEnvPrefix(
  user?: Partial<User>,
): number | string | undefined {
  const siteDomain = window.location.hostname;
  const isProd = siteDomain.indexOf('clients.evisort') > -1;
  const isLocal = siteDomain.includes(LOCAL_DOMAIN);
  if (isProd || isLocal) {
    return user?.client;
  } else {
    return `${siteDomain.split('.').slice(-3)[0]}-${user?.client}`;
  }
}

export function getUserClientInfo(user: User): string {
  return `${user.id}_${user.client}`;
}

export function getUserClientId(user?: Partial<User>): number | undefined {
  return user?.client;
}

export function testIsAdmin(user: Partial<User>): boolean {
  return (
    [UserRoleType.Admin].includes(user.role as UserRoleType) ||
    !!user.is_platform_admin
  );
}

export function testIsEverestAdmin(user: Partial<User>): boolean {
  return testIsAdmin(user) || !!user.is_workflow_admin;
}

export function testIsSuperAdmin(user: Partial<User>): boolean {
  return !!user.is_platform_admin;
}

export function testIsReviewer(user: Partial<User>): boolean {
  return [UserRoleType.Reviewer].includes(user.role as UserRoleType);
}

export function externalUserToUser(externalUser: {
  name?: string | null;
  email?: string | null;
}): { email: string; firstName: string; lastName: string; id: string } {
  // this will also catch the case where name and email are an empty string (e.g '')
  const name = externalUser.name || 'External User';
  const email = externalUser.email || 'Unknown Email';
  const nameParts = name.split(' ');
  const firstName = nameParts[0];
  // if there is more than 2 names, use all the names after the first as the lastName
  const lastName = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';
  return { email, firstName, lastName, id: '' };
}

export function getClientDomainWindowLocation(user: Partial<User>): string {
  return `${window.location.origin}/${user.client_config?.sub_domain}`;
}

/**
 * Returns the users' ids for the given emails.
 */
export function getUserIdsFromEmails(
  emails: string[],
  users: Record<PilotId, Partial<User>>,
): PilotId[] {
  let usersIds: PilotId[] = [];
  if (emails.length > 0) {
    usersIds = Object.values(users)
      .filter((user) => emails.includes(user.email as string))
      .map((user) => user.id as PilotId);
  }
  return usersIds;
}

export const withEdsUserFields = <T extends Partial<User>>(currentUser: T) => {
  return {
    ...currentUser,
    firstName: currentUser.first_name,
    lastName: currentUser.last_name,
    avatar: currentUser.avatar,
  };
};

export const getUserProfileMenuItems = (_currentUser: Partial<User>) => {
  const settingsMenuItem = {
    label: 'Settings',
    value: 'profileSettings',
  };
  const myDeletedItemsMenuItem = {
    label: 'My Deleted Items',
    value: 'myDeletedItems',
  };
  const logoutMenuItem = {
    label: 'Log Out',
    value: 'logOut',
  };

  return [settingsMenuItem, myDeletedItemsMenuItem, logoutMenuItem];
};

export const isEvisortUser = (user?: Partial<User> | null): boolean => {
  return !!user?.email?.endsWith('@evisort.com');
};

export const getUserNameFromFullname = (
  fullname?: string | null,
): { firstName: string; lastName: string } => {
  if (!fullname) {
    return { firstName: '', lastName: '' };
  }

  const parts = fullname.split(' ');

  if (parts.length > 1) {
    return { firstName: parts[0], lastName: parts[parts.length - 1] };
  }

  return { firstName: fullname, lastName: '' };
};
